/* The chrome, the routes, and the one thing on this site that must never be
 * unmounted.
 *
 * THE PLAYER IS MOUNTED HERE, ONCE, AND OUTSIDE THE ROUTER'S OUTPUT.
 * A radio station whose audio stops when you click "Archive" is not a radio
 * station, it is a page with a play button on it. So PlayerProvider wraps
 * everything, PlayerBar sits outside <Routes>, and no navigation in this app
 * can reach the <audio> element. This is the whole reason the routing lives in
 * one file instead of being spread across the pages.
 *
 * Everything else in here is the frame: the 10px rule around the page, the nav,
 * the ticker, the footer, a skip link that actually goes somewhere, and two
 * failure surfaces written in the station's voice — a 404 and an error boundary.
 * A white screen is the one response we are not willing to give someone.
 */

import { Component, Suspense, lazy, useEffect } from 'react';
import type { CSSProperties, ErrorInfo, ReactNode } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';

import { Footer, Nav, Ticker } from './components';
import { AccountProvider } from './account';
import { ChatProvider, LiveChatPanel } from './chat';
import { PlayerBar } from './player/PlayerBar';
import { PlayerProvider } from './player/PlayerProvider';

import Account from './pages/Account';
import Archive from './pages/Archive';
import Episode from './pages/Episode';
import Home from './pages/Home';
import NotFound from './pages/NotFound';
import Premiere from './pages/Premiere';
import Premieres from './pages/Premieres';
import Resident from './pages/Resident';
import Residents from './pages/Residents';
import Schedule from './pages/Schedule';
import Submit from './pages/Submit';

/* The desk is staff-only and nobody arrives here from a link. Two chunks the
   public never downloads, and the queue is the heaviest page in the app. */
const Admin = lazy(() => import('./pages/Admin'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const Cue = lazy(() => import('./pages/Cue'));
/* The heaviest page in the app now: four tabs of forms, a paste parser and a
   file browser. Lazy for the same reason as the others, and more so. */
const Studio = lazy(() => import('./pages/Studio'));

/** The section rhythm from base.css, for the few blocks that live in this file. */
const PAD: CSSProperties = {
  padding: '28px clamp(12px, 4vw, 28px) 40px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
};

const ACTIONS: CSSProperties = {
  display: 'flex',
  gap: '16px',
  flexWrap: 'wrap',
  marginTop: '6px',
};

/* ------------------------------------------------------------------- states */

/**
 * The wait while an admin chunk arrives. Named, not a spinner: a spinner says
 * "something is happening", a word says which something.
 */
function Loading({ label }: { label: string }) {
  return (
    <div style={PAD} role="status">
      <div className="mono dim">{label}</div>
    </div>
  );
}

/** No route matched. */
/**
 * What a caught render error looks like.
 *
 * It says what broke, that it is our fault, and that the audio is unaffected —
 * which is the fact the person actually wants, because they are probably
 * listening to something right now.
 */
function PageFailed({ error }: { error: Error }) {
  return (
    <>
      <section className="sec">
        <h2>This page stopped</h2>
        <div className="mono dim">CLIENT ERROR</div>
      </section>
      <div style={PAD}>
        <p className="t-body">
          Something in this page failed while it was being drawn, so we stopped rather than show
          you half of it. That is a fault at our end, not anything you did.
        </p>
        <p className="t-body">
          The stream is unaffected — the player at the bottom of the window is still running.
          Reloading usually clears it. If it keeps happening, tell us at the address in the footer
          and say which page you were on.
        </p>
        <p className="mono dim" style={{ margin: 0, lineHeight: 1.6, letterSpacing: '.06em' }}>
          {error.message || 'No message was attached to the error.'}
        </p>
        <div style={ACTIONS}>
          <button type="button" className="btn" onClick={() => window.location.reload()}>
            Reload this page
          </button>
          <Link className="btn-ghost" to="/">
            Back to the front page
          </Link>
        </div>
      </div>
    </>
  );
}

/* ---------------------------------------------------------- error boundary */

interface BoundaryProps {
  /**
   * Changing this clears a caught error. It is the pathname, so walking away
   * from a broken page fixes it — which is what everybody tries first anyway.
   */
  resetKey: string;
  children: ReactNode;
}

interface BoundaryState {
  error: Error | null;
}

class ErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Nothing to report to yet, but the console is where the next person to
    // debug this will look, and the component stack is the useful half.
    console.error('[hgr] a page failed to render', error, info.componentStack);
  }

  componentDidUpdate(previous: BoundaryProps): void {
    // Reset on navigation WITHOUT keying the subtree: a key would remount the
    // page on every route change and throw away the schedule's day selection
    // and the archive's filters along with it.
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render(): ReactNode {
    if (this.state.error) return <PageFailed error={this.state.error} />;
    return this.props.children;
  }
}

/* ------------------------------------------------------------------- routes */

/**
 * Back to the top on navigation, because the browser will not do it for a
 * client-side route change and page two of the archive otherwise opens
 * halfway down. A hash is left alone — that is somebody linking to a section.
 */
/** Routes that get no scrolling marquee. See the note at its render site. */
const QUIET_ROUTES = new Set(['/submit', '/account']);

function useScrollReset(pathname: string): void {
  useEffect(() => {
    if (window.location.hash) return;
    window.scrollTo(0, 0);
  }, [pathname]);
}

export default function App() {
  const { pathname } = useLocation();
  useScrollReset(pathname);

  return (
    <AccountProvider>
      <PlayerProvider>
        <ChatProvider>
        <a className="skip" href="#main">
          Skip to the programme
        </a>

        <div className="frame">
          <Nav />
        {/* The marquee is deliberately OFF on the submissions form and on
            anything asking for a decision. A form is the one place where a
            mis-click is expensive: keep the borders and the type, drop the
            motion. The page puts a static status bar in its place. */}
        {QUIET_ROUTES.has(pathname) ? null : <Ticker />}

        <main id="main" tabIndex={-1}>
          <ErrorBoundary resetKey={pathname}>
            <Routes>
              <Route path="/" element={<Home />} />

              <Route path="/schedule" element={<Schedule />} />
              {/* The same page. The date is station-local YYYY-MM-DD, so a
                  schedule link survives being pasted into another timezone. */}
              <Route path="/schedule/:date" element={<Schedule />} />

              <Route path="/archive" element={<Archive />} />
              {/* A genre is the archive with one chip already pressed, not a
                  separate page — the filters and the grid are the same thing. */}
              <Route path="/genre/:slug" element={<Archive />} />
              <Route path="/episodes/:catalogueNo" element={<Episode />} />

              {/* Premieres are NOT episodes and do not live under /episodes.
                  An episode is a broadcast; a premiere is one unreleased record
                  the station played before it came out, and under D14 the two
                  are cleared on completely different grounds — see the note
                  above `PremiereCard` in types.ts. Separate object, separate
                  address, and the slug is the label's, not a catalogue number. */}
              <Route path="/premieres" element={<Premieres />} />
              <Route path="/premieres/:slug" element={<Premiere />} />

              <Route path="/residents" element={<Residents />} />
              <Route path="/residents/:slug" element={<Resident />} />

              <Route path="/account" element={<Account />} />
              <Route path="/submit" element={<Submit />} />

              <Route
                path="/admin"
                element={
                  <Suspense fallback={<Loading label="Opening the desk…" />}>
                    <Admin />
                  </Suspense>
                }
              />
              <Route
                path="/admin/login"
                element={
                  <Suspense fallback={<Loading label="Opening the desk…" />}>
                    <AdminLogin />
                  </Suspense>
                }
              />
              {/* The cue desk. Staff only, and the endpoint behind it is too —
                  see hgr/routers/cue.py for why it ignores clearance. */}
              <Route
                path="/admin/cue"
                element={
                  <Suspense fallback={<Loading label="Opening the cue desk…" />}>
                    <Cue />
                  </Suspense>
                }
              />
              {/* The operator's desk: episodes, premieres, residents and shows,
                  each with a form that saves as you type. Staff only, like the
                  other two, and gated on /admin/me the same way. */}
              <Route
                path="/admin/studio"
                element={
                  <Suspense fallback={<Loading label="Opening the studio…" />}>
                    <Studio />
                  </Suspense>
                }
              />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </ErrorBoundary>
        </main>

          <Footer />
        </div>

        {/* Both live surfaces sit outside the frame and router. The audio
            remains the only media element; chat keeps its room and transcript
            while somebody follows a link elsewhere on the station. */}
        <PlayerBar />
        <LiveChatPanel />
        </ChatProvider>
      </PlayerProvider>
    </AccountProvider>
  );
}
